import { Button } from '@/components/ui/button';
import { Media } from '@/Types/gql/graphql';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSwipeable } from 'react-swipeable';

interface MediaGalleryProps {
  mediaData: Pick<Media, '_id' | 'path' | 'type' | 'thumbnail'>[];
  selectedMedia: number | null;
  setSelectedMedia: React.Dispatch<React.SetStateAction<number | null>>;
}

export default function MediaGallery({
  mediaData,
  selectedMedia,
  setSelectedMedia,
}: MediaGalleryProps) {
  const [currentIndex, setCurrentIndex] = useState(selectedMedia || 0);

  const goToNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % mediaData.length);
  };

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + mediaData.length) % mediaData.length);
  };

  const handlers = useSwipeable({
    onSwipedLeft: goToNext,
    onSwipedRight: goToPrevious,
    trackMouse: true,
  });

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'ArrowRight') {
      goToNext();
    } else if (event.key === 'ArrowLeft') {
      goToPrevious();
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentIndex]);

  const currentMedia = mediaData[currentIndex];

  return (
    <div className='fixed w-full h-full inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
      <div
        {...handlers}
        className='flex items-center justify-center relative w-full h-full bg-black bg-opacity-80 rounded-lg overflow-hidden'
      >
        {currentMedia.type === 'IMAGE' ? (
          <img
            src={currentMedia.path || ''}
            alt={`Media ${currentIndex + 1}`}
            className='w-full h-full object-contain'
          />
        ) : (
          <video
            src={currentMedia?.path || ''}
            controls
            autoPlay
            controlsList='nodownload'
            className='h-full w-[1060px] max-w-[98%] object-contain'
          />
        )}
        <Button
          id='media-gallery-prev-btn'
          variant='outline'
          size='icon'
          className='absolute top-1/2 left-4 transform -translate-y-1/2 bg-white bg-opacity-50 hover:bg-opacity-75'
          onClick={goToPrevious}
        >
          <ChevronLeft className='h-4 w-4' />
        </Button>
        <Button
          id='media-gallery-next-btn'
          variant='outline'
          size='icon'
          className='absolute top-1/2 right-4 transform -translate-y-1/2 bg-white bg-opacity-50 hover:bg-opacity-75 z-30'
          onClick={goToNext}
        >
          <ChevronRight className='h-4 w-4' />
        </Button>
        <Button
          id='media-galler-close-btn'
          onClick={() => setSelectedMedia(null)}
          className='absolute top-6 -right-4'
          variant='ghost'
        >
          <X className='h-10 w-10 text-white' />
        </Button>
      </div>
    </div>
  );
}
